import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from './AdminLayout';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from '../../hooks/use-toast';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  X,
  GripVertical
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServicesPage = () => {
  const { token } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    icon: 'code',
    items: [],
    href: '/services',
    order: 0
  });
  const [itemsText, setItemsText] = useState('');

  const iconOptions = ['database', 'smartphone', 'globe', 'server', 'code', 'cloud'];

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData(service);
      setItemsText(service.items?.join('\n') || '');
    } else {
      setEditingService(null);
      setFormData({ title: '', icon: 'code', items: [], href: '/services', order: services.length });
      setItemsText('');
    }
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...formData,
        items: itemsText.split('\n').filter(i => i.trim())
      };
      
      if (editingService) {
        await axios.put(`${API}/admin/services/${editingService.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Updated!', description: 'Service updated successfully.' });
      } else {
        await axios.post(`${API}/admin/services`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Created!', description: 'Service created successfully.' });
      }
      setModalOpen(false);
      fetchServices();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save service.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await axios.delete(`${API}/admin/services/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Deleted!', description: 'Service deleted successfully.' });
      fetchServices();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete service.', variant: 'destructive' });
    }
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Services</h1>
            <p className="text-gray-500 mt-1">Manage your service offerings</p>
          </div>
          <Button
            onClick={() => openModal()}
            className="bg-gradient-to-r from-[#ff6b35] to-[#ff8f5c] hover:from-[#e55a2b] hover:to-[#ff6b35]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Service
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#ff6b35]" />
          </div>
        ) : services.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
            <p className="text-gray-500">No services yet. Add your first service!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <div key={service.id} className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#ff6b35]/10 flex items-center justify-center">
                    <span className="text-[#ff6b35] text-xl">💼</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openModal(service)} className="p-2 hover:bg-gray-100 rounded-lg">
                      <Pencil className="w-4 h-4 text-gray-500" />
                    </button>
                    <button onClick={() => handleDelete(service.id)} className="p-2 hover:bg-red-50 rounded-lg">
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{service.title}</h3>
                <ul className="space-y-1">
                  {service.items?.slice(0, 3).map((item, i) => (
                    <li key={i} className="text-sm text-gray-500 flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#ff6b35]"></span>
                      {item}
                    </li>
                  ))}
                  {service.items?.length > 3 && (
                    <li className="text-sm text-gray-400">+{service.items.length - 3} more</li>
                  )}
                </ul>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingService ? 'Edit Service' : 'Add Service'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="SAP Services"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                <div className="flex gap-2 flex-wrap">
                  {iconOptions.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon })}
                      className={`px-3 py-2 rounded-lg border text-sm ${
                        formData.icon === icon
                          ? 'border-[#ff6b35] bg-[#ff6b35]/10 text-[#ff6b35]'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Items (one per line)</label>
                <Textarea
                  value={itemsText}
                  onChange={(e) => setItemsText(e.target.value)}
                  placeholder="SAP S/4 HANA Migration&#10;SAP Implementation&#10;SAP Consulting"
                  rows={5}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setModalOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} className="flex-1 bg-[#ff6b35] hover:bg-[#e55a2b]">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingService ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default ServicesPage;
